import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "../api";
import { Phone, Star, Award, Heart, ShieldCheck } from "lucide-react";
import { Button } from "../components/ui/button";
import { useAuth } from "../auth";
import { toast } from "sonner";
import ReportDialog from "../components/ReportDialog";
import AddressActions from "../components/AddressActions";
import PoolSuggestEditDialog from "../components/PoolSuggestEditDialog";

export default function PoolDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [pool, setPool] = useState(null);
  const [isFav, setIsFav] = useState(false);

  const reloadFav = () => {
    if (!user || user.role !== "client") return;
    api.get("/favorites").then((r) => setIsFav(r.data.some((f) => f.target_type === "pool" && f.target_id === id))).catch(() => {});
  };

  useEffect(() => {
    api.get(`/pools/${id}`).then((r) => setPool(r.data));
    reloadFav();
    // eslint-disable-next-line
  }, [id, user?.user_id]);

  const toggleFav = async () => {
    if (!user) { toast.error("Connectez-vous pour ajouter aux favoris"); nav("/login"); return; }
    if (isFav) {
      await api.delete("/favorites", { params: { target_type: "pool", target_id: id } });
      toast.success("Retiré des favoris");
    } else {
      await api.post("/favorites", { target_type: "pool", target_id: id });
      toast.success("Ajouté aux favoris");
    }
    reloadFav();
  };

  if (!pool) return <div className="p-12 text-center text-slate-500">Chargement...</div>;
  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-8 py-8">
      <div className="card-soft p-6 mb-8">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>{pool.name}</h1>
            <div className="text-slate-600 mt-2">
              <AddressActions address={pool.address} lat={pool.lat} lng={pool.lng} />
            </div>
            {pool.phone && <div className="text-slate-600 flex items-center gap-2 mt-1"><Phone className="w-4 h-4" /> {pool.phone}</div>}
          </div>
          {user?.role === "client" && (
            <Button onClick={toggleFav} variant="outline" className={`rounded-full ${isFav ? "border-rose-400 text-rose-600" : ""}`} data-testid="fav-pool-btn">
              <Heart className={`w-4 h-4 mr-1 ${isFav ? "fill-rose-500 text-rose-500" : ""}`} />
              {isFav ? "En favori" : "Mettre en favori"}
            </Button>
          )}
          {user && (
            <PoolSuggestEditDialog pool={pool} />
          )}
          {user && (
            <ReportDialog
              targetType="pool"
              targetId={id}
              targetLabel={`${pool.name} — ${pool.address}`}
            />
          )}
        </div>
      </div>
      <h2 className="text-2xl font-bold text-slate-900 mb-4" style={{ fontFamily: "Outfit" }}>Maîtres-nageurs disponibles</h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {pool.mns_list?.length ? pool.mns_list.map(({ user: u, profile, boosted, verified }) => (
          <Link key={u.user_id} to={`/mns/${u.user_id}`} className="card-soft p-5 hover:-translate-y-0.5 transition-all relative" data-testid={`mns-card-${u.user_id}`}>
            <div className="absolute top-3 right-3 flex gap-1">
              {verified && (
                <span className="text-[10px] inline-flex items-center gap-0.5 bg-gradient-to-r from-emerald-500 to-sky-600 text-white px-2 py-0.5 rounded-full font-semibold" title="Profil vérifié (SIRET, assurance et diplôme)" data-testid={`verified-${u.user_id}`}>
                  <ShieldCheck className="w-3 h-3" />Vérifié
                </span>
              )}
              {boosted && <span className="text-[10px] bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full font-semibold">⭐ Boosté</span>}
            </div>
            <div className="flex items-center gap-3">
              <img src={u.picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(profile.first_name + ' ' + profile.last_name)}&background=0369A1&color=fff`} alt="" className="w-14 h-14 rounded-full object-cover ring-2 ring-sky-200" />
              <div>
                <div className="font-semibold text-slate-900">{profile.first_name} {profile.last_name}</div>
                <div className="text-xs text-slate-500 flex items-center gap-1"><Award className="w-3 h-3" />MNS certifié</div>
              </div>
            </div>
            <div className="mt-3 text-sm text-slate-600 line-clamp-2">{profile.bio || "Cours particuliers de natation"}</div>
            <div className="mt-3 flex items-center justify-between">
              <span className="text-sky-700 font-bold">{profile.hourly_rate}€/h</span>
              {/* avg rating fetched via separate endpoint omitted for brevity here */}
              <span className="text-xs text-slate-500 flex items-center gap-1"><Star className="w-3 h-3 text-amber-500" />Voir profil</span>
            </div>
          </Link>
        )) : <div className="text-slate-500 col-span-full">Aucun MNS rattaché à cette piscine pour l'instant.</div>}
      </div>
    </div>
  );
}
